import type { Apis, SessionService } from '@efdigital/ll-addon-auth';
import { default as BaseCoacheeService } from '@efdigital/ll-addon-coaching/services/coachee';
import { canTransitionToStatus } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import {
  type Coaching,
  type CoachingPartnership,
  type CoachingPartnershipStatus,
  type CoachingPlan,
  type CoachingSession,
  type CoachingSessionType,
  type Cohort,
  DataTypeCoaching,
} from '@efdigital/ll-addon-data';
import type { CoachMatch } from '@efdigital/ll-cdk-coaching-ai-matching-api-types';
import type {
  CoacheePostAttendanceMarkRequestBody,
  CoacheePostFeedbackRequestBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';
import config from '@efdigital/ll-xceed-v2/config/environment';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import { asAsyncBelongsTo } from '../data/async-setter.ts';
import {
  buildCoachMatchesRequest,
  parseCoachMatchesResponse,
} from '../data/builders/coaching/coach-matches.ts';
import {
  buildCoacheeGiveFeedbackRequest,
  parseCoacheeGiveFeedbackResponse,
} from '../data/builders/coaching/coaching-feedback.ts';
import {
  createCoachingPartnership,
  queryCoachingPartnershipsByCoachingPlan,
  queryCoachingPartnershipsByCoachingPlanAndCoach,
  updateCoachingPartnership,
} from '../data/builders/coaching/coaching-partnerships.ts';
import {
  createCoachingPlan,
  queryCoachingPlanByCoachee,
} from '../data/builders/coaching/coaching-plans.ts';
import {
  buildCoacheeBookSessionRequest,
  buildCoacheeCancelSessionRequest,
  buildCoacheeMarkAttendanceRequest,
} from '../data/builders/coaching/sessions.ts';
import {
  buildCohortAnnouncementsRequest,
  type CohortAnnouncement,
  parseCohortAnnouncementsResponse,
} from '../data/builders/xceed/messages.ts';
import type ProgramsService from './programs';

const apis: Apis = config['ll-addon-auth-apis'];

export default class CoacheeService extends BaseCoacheeService {
  apis = apis;

  @service declare programs: ProgramsService;
  @service declare session: SessionService;
  @service declare store: Store;

  #cohortsWithCoaching: {
    exists: boolean;
    cohortsWithCoaching: Cohort[];
  } | null = null;

  async #ensureCachedCoachings() {
    if (this.#cohortsWithCoaching) {
      return this.#cohortsWithCoaching;
    }

    const cohorts = await this.programs.cohorts();
    const cohortsWithCoaching = cohorts.filter((cohort) => cohort.coaching?.id);
    this.#cohortsWithCoaching = {
      exists: cohortsWithCoaching.length > 0,
      cohortsWithCoaching,
    };
    return this.#cohortsWithCoaching;
  }

  async isCoachee() {
    const status = await this.#ensureCachedCoachings();
    return status.exists && status.cohortsWithCoaching.length > 0;
  }

  async cohortsWithCoaching() {
    return (await this.#ensureCachedCoachings()).cohortsWithCoaching;
  }

  async ensureCoachingPlan(coaching: Coaching): Promise<CoachingPlan> {
    const coacheeUserId = (await this.session.getCurrent()).userId!;
    const existingPlans = await this.store.request(
      queryCoachingPlanByCoachee(coacheeUserId, coaching.id!)
    );
    if (existingPlans.content.data.length) {
      return existingPlans.content.data[0]!;
    }

    const newPlan = this.store.createRecord<CoachingPlan>(
      DataTypeCoaching.CoachingPlan,
      {
        user: coacheeUserId,
        coaching: asAsyncBelongsTo(coaching),
      }
    );

    await this.store.request(createCoachingPlan(newPlan, this.store));
    return newPlan;
  }

  async getAllPartnershipsForCoachingPlan(coachingPlan: CoachingPlan) {
    const coachingPartnershipQuery = queryCoachingPartnershipsByCoachingPlan(
      coachingPlan.id!
    );
    const findExisting = await this.store.request(coachingPartnershipQuery);
    return findExisting.content.data;
  }

  async getPartnership(coachingPlan: CoachingPlan, coachUserId: string) {
    const coachingPartnershipQuery =
      queryCoachingPartnershipsByCoachingPlanAndCoach(
        coachingPlan.id!,
        coachUserId
      );
    const findExisting = await this.store.request(coachingPartnershipQuery);
    if (findExisting.content.data.length) {
      const existing = findExisting.content.data[0]!;
      return existing;
    }

    const newPartnership = this.store.createRecord<CoachingPartnership>(
      DataTypeCoaching.CoachingPartnership,
      {
        coachingPlan: asAsyncBelongsTo(coachingPlan),
        coachUserId: coachUserId,
        user: coachingPlan.user,
        status: 'pending',
      }
    );

    return newPartnership;
  }

  async setStatusForCoach(
    coachingPlan: CoachingPlan,
    coachUserId: string,
    status: CoachingPartnershipStatus
  ) {
    const allPartnerships =
      await this.getAllPartnershipsForCoachingPlan(coachingPlan);
    let partnership = allPartnerships.find(
      (partnership) => partnership.coachUserId === coachUserId
    );
    if (!partnership) {
      partnership = this.store.createRecord<CoachingPartnership>(
        DataTypeCoaching.CoachingPartnership,
        {
          coachingPlan: asAsyncBelongsTo(coachingPlan),
          coachUserId: coachUserId,
          user: coachingPlan.user,
          status: 'pending',
        }
      );
    }

    const existingCoachHasBeenChosen = allPartnerships.some(
      (partnership) => partnership.status === 'chosen'
    );

    if ((partnership.status as string) === 'reassigned') {
      throw new Error('An administrator has moved this coachee off this coach');
    }

    const canTransition = canTransitionToStatus(
      partnership.status,
      status,
      existingCoachHasBeenChosen
    );
    if (canTransition.outcome === 'blocked') {
      throw new Error(canTransition.reason);
    }

    if (canTransition.outcome === 'allowed') {
      partnership.status = status;
      if (partnership.isNew) {
        await this.store.request(
          createCoachingPartnership(partnership, this.store)
        );
      } else {
        await this.store.request(
          updateCoachingPartnership(partnership, this.store)
        );
      }
    }
  }

  async bookSession(
    partnership: CoachingPartnership,
    sessionType: CoachingSessionType,
    idempotencyKey: string,
    startTime: string,
    endTime: string,
    notes?: string
  ) {
    const trimmedNotes = notes?.trim();
    const query = await buildCoacheeBookSessionRequest(
      this.apis,
      this.session,
      idempotencyKey,
      {
        partnershipId: partnership.id!,
        sessionType,
        startTime,
        endTime,
        ...(trimmedNotes && { notes: trimmedNotes }),
      }
    );

    const response = await fetch(query);
    if (!response.ok) {
      let message = 'Failed to book session';

      try {
        const body = (await response.json()) as { message?: string };
        if (body.message) {
          message = body.message;
        }
      } catch {
        /* empty */
      }

      throw new Error(message);
    }
  }

  async cancelSession(coachingSession: CoachingSession, cancelReason: string) {
    const query = await buildCoacheeCancelSessionRequest(
      this.apis,
      this.session,
      coachingSession,
      { cancelReason }
    );

    const response = await fetch(query);
    if (!response.ok) {
      throw new Error('Failed to cancel session');
    }
  }

  async markAttendance(
    coachingSession: CoachingSession,
    body: CoacheePostAttendanceMarkRequestBody
  ) {
    const query = await buildCoacheeMarkAttendanceRequest(
      this.apis,
      this.session,
      coachingSession,
      body
    );

    const response = await fetch(query);
    if (!response.ok) {
      throw new Error('Failed to confirm attendance');
    }
  }

  async giveFeedback(
    coachingPartnershipId: string,
    body: CoacheePostFeedbackRequestBody
  ) {
    const query = await buildCoacheeGiveFeedbackRequest(
      this.apis,
      this.session,
      coachingPartnershipId,
      body
    );

    // Throws `FeedbackAlreadySubmittedError` on a 409, so the planner can
    // treat a resubmission as done.
    return parseCoacheeGiveFeedbackResponse(await fetch(query));
  }

  #cachedRelevanceMatches: Record<string, CoachMatch[]> = {};

  flushCachedRelevanceMatches(cohortId: string) {
    if (!this.#cachedRelevanceMatches[cohortId]) {
      return;
    }

    delete this.#cachedRelevanceMatches[cohortId];
  }

  async tryGetRelevanceMatches(cohortId: string): Promise<CoachMatch[]> {
    if (this.#cachedRelevanceMatches[cohortId]) {
      return this.#cachedRelevanceMatches[cohortId];
    }

    const request = await buildCoachMatchesRequest(
      apis,
      this.session,
      cohortId
    );
    const response = await fetch(request);
    const matches = await parseCoachMatchesResponse(response);
    this.#cachedRelevanceMatches[cohortId] = matches;
    return matches;
  }

  async getCohortAnnouncements(
    programId: string,
    cohortId: string
  ): Promise<CohortAnnouncement[]> {
    const request = await buildCohortAnnouncementsRequest(
      apis,
      this.session,
      programId,
      cohortId
    );
    const response = await fetch(request);
    return parseCohortAnnouncementsResponse(response);
  }
}
